import { useCallback, useRef, useState } from 'react';

/**
 * Utility hook to make writing save data request hooks faster
 */
export const useSaveDataRequest = <T>() => {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [isSaving, setIsSaving] = useState(false);
  const req = useRef<Promise<T>>();

  /**
   * Track the given save request (promise) and update the state
   * with loading flags / return data / error data (when available).
   */
  const save = useCallback(async (thisReq: Promise<T>) => {
    req.current = thisReq;
    setData(undefined);
    setError(undefined);

    try {
      setIsSaving(true);
      const newData = await thisReq;

      if (thisReq === req.current) {
        setData(newData);
        return newData;
      }
    } catch (e) {
      if (thisReq === req.current) {
        setError(e);
      }
    } finally {
      if (thisReq === req.current) {
        setIsSaving(false);
      }
    }
  }, []);

  /**
   * Will reset all state back to default values
   */
  const reset = useCallback(() => {
    setData(undefined);
    setError(undefined);
    setIsSaving(false);
    req.current = undefined;
  }, []);

  return {
    data,
    error,
    isSaving,
    reset,
    save
  };
};
